import React, { useState } from 'react';
import { authService } from '../../services/authService';
import { RegisterCredentials } from '../../types/auth';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Field, Input } from '../ui';
import { AUTH_BUTTON, AUTH_LINK, AuthActions, AuthFootnote, AuthScreen, FormNotice, PasswordInput } from './AuthShell';

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<RegisterCredentials>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email address';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (confirmPassword !== form.password) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    if (!validate()) return;
    try {
      setLoading(true);
      const res = await authService.register({
        email: form.email,
        password: form.password,
        firstName: form.firstName || undefined,
        lastName: form.lastName || undefined,
      });
      setSubmitSuccess(res.message);
      // Redirect to login after short delay
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: any) {
      console.error('Registration error:', err);
      let message = 'Registration failed. Please try again.';
      
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.response?.status === 409) {
        message = 'An account with this email already exists. Please use a different email or try logging in.';
      } else if (err?.response?.status === 400) {
        message = 'Invalid data provided. Please check your input and try again.';
      } else if (err?.response?.status >= 500) {
        message = 'Server error occurred. Please try again later.';
      } else if (err?.code === 'NETWORK_ERROR' || !err?.response) {
        message = 'Network error. Please check your connection and try again.';
      }
      
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthScreen title="Create account" meta="Requires super admin approval" aside>
      <form className="flex flex-1 flex-col" onSubmit={handleSubmit} noValidate>
        <div className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" htmlFor="firstName" error={errors.firstName}>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={form.firstName}
                onChange={handleChange}
                placeholder="Optional"
              />
            </Field>
            <Field label="Last name" htmlFor="lastName" error={errors.lastName}>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Optional"
              />
            </Field>
          </div>
          <Field label="Email" htmlFor="email" error={errors.email}>
            <Input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={form.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              placeholder="you@company.com"
            />
          </Field>
          <Field label="Password" htmlFor="password" error={errors.password}>
            <PasswordInput
              id="password"
              name="password"
              autoComplete="new-password"
              required
              value={form.password}
              onChange={handleChange}
              aria-invalid={!!errors.password}
              placeholder="6+ characters"
            />
          </Field>
          <Field label="Confirm password" htmlFor="confirmPassword" error={errors.confirmPassword}>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={handleChange}
              aria-invalid={!!errors.confirmPassword}
            />
          </Field>
        </div>

        <AuthActions>
          {submitError && <FormNotice>{submitError}</FormNotice>}
          {submitSuccess && <FormNotice tone="success">{submitSuccess}</FormNotice>}
          <Button type="submit" variant="primary" loading={loading} className={AUTH_BUTTON}>
            {loading ? 'Submitting…' : 'Create account'}
          </Button>
          <AuthFootnote>
            Have an account?{' '}
            <Link to="/login" className={AUTH_LINK}>
              Sign in
            </Link>
          </AuthFootnote>
        </AuthActions>
      </form>
    </AuthScreen>
  );
};
